import React from "react";
import {
    InfoCircleOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    ApiOutlined,
    BellOutlined,
    UserOutlined,
    CalendarOutlined
} from "@ant-design/icons";
import { Col, Row, Tag, Empty } from "antd";
import CommonCard from "../../../global/card";
import { capitalizeFirstLetter, formatDateTime } from "../../../../utils/functions";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import CommonLoader from "../../../global/loader";

const EnhancedLeadSourceDetails = ({
    leadSourceDetail: initialData,
}) => {
    const { data: leadSourceDetail, isLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.leadSource.item({ id: initialData?._id }),
        queryParams: { id: initialData?._id },
        endpoint: "leadsource",
        enabled: !!initialData?._id
    });

    if (isLoading) {
        return <CommonLoader text="Loading lead source details..." minHeight="400px" />;
    }

    if (!leadSourceDetail) return <div className="p-6"><Empty description="No details found" /></div>;

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" } })}
            <span style={{ fontSize: "clamp(13px, 4vw, 15px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const InfoItem = ({ label, value, isTag, color, icon, span = 12 }) => (
        <Col xs={24} sm={span}>
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                    <span style={{ color: "var(--primary-color, #1890ff)" }}>{icon}</span>
                    {label}
                </span>
                {isTag ? (
                    <Tag color={color} className="m-0 px-3 py-1 font-medium capitalize w-fit">
                        {value || "—"}
                    </Tag>
                ) : (
                    <span className="text-sm font-semibold text-gray-900">{value || "—"}</span>
                )}
            </div>
        </Col>
    );

    return (
        <div className="sm:p-6 p-2 w-full min-h-full overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <GlobalOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "clamp(16px, 5vw, 18px)" }} />
                        <span style={{ fontSize: "clamp(14px, 5vw, 16px)", fontWeight: 600 }}>Lead Source Details</span>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Basic Information Section */}
                    <Col span={24} className="border-b border-gray-200 pb-4">
                        <SectionHeader icon={<InfoCircleOutlined />} title="Basic Information" />
                        <Row gutter={[{ xs: 12, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Job Name"
                                value={leadSourceDetail.jobName}
                                icon={<InfoCircleOutlined />}
                            />
                            <InfoItem
                                label="Lead Source"
                                value={capitalizeFirstLetter(leadSourceDetail.leadSource)}
                                icon={<GlobalOutlined />}
                            />
                            <InfoItem
                                label="Status"
                                value={leadSourceDetail.active ? 'Active' : 'Inactive'}
                                isTag
                                color={leadSourceDetail.active ? 'success' : 'error'}
                                icon={<CheckCircleOutlined />}
                            />
                            <InfoItem
                                label="Created At"
                                value={formatDateTime(leadSourceDetail.createdAt)}
                                icon={<CalendarOutlined />}
                            />
                        </Row>
                    </Col>


                    {/* Destinations Section */}
                    {leadSourceDetail.destinations && leadSourceDetail.destinations.length > 0 && (
                        <Col span={24} className="border-b border-gray-200 py-4">
                            <SectionHeader icon={<ApiOutlined />} title="Destinations" />
                            <div className="flex flex-wrap gap-2 mb-4">
                                {leadSourceDetail.destinations.map((dest, index) => (
                                    <Tag key={index} color="blue" className="px-3 py-1 text-sm">
                                        {capitalizeFirstLetter(dest)}
                                    </Tag>
                                ))}
                            </div>
                        </Col>
                    )}

                    {/* Alert Configuration Section */}
                    <Col span={24} className="py-4">
                        <SectionHeader icon={<BellOutlined />} title="Alert Configuration" />
                        <Row gutter={[{ xs: 12, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Alert Send Type"
                                value={leadSourceDetail.alertSendType === 'specifictime' ? 'Specific Time' : 'Anytime'}
                                icon={<BellOutlined />}
                            />
                            {leadSourceDetail.alertSendType === 'specifictime' && (
                                <>
                                    <InfoItem
                                        label="Time Window"
                                        value={`${leadSourceDetail.alertTime?.startTime} - ${leadSourceDetail.alertTime?.endTime}`}
                                        icon={<CalendarOutlined />}
                                    />
                                    <InfoItem
                                        label="Timezone"
                                        value={leadSourceDetail.alertTime?.timezone}
                                        icon={<GlobalOutlined />}
                                    />
                                </>
                            )}
                            <InfoItem
                                label="Alert Recipients"
                                value={`${Array.isArray(leadSourceDetail.alertId) ? leadSourceDetail.alertId.length : 0} User(s)`}
                                icon={<UserOutlined />}
                            />
                        </Row>
                    </Col>
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedLeadSourceDetails;
